import InputTypesEnum from '../../../Enums/InputTypesEnum';
import LOOKUP_SELECT_CONFIG from './lookupSelectConfig';
import {
  renderNumberStepper,
  renderTextField,
  renderTextField2,
  renderImportListTextField,
  renderTextBox,
  renderInputWithCheckBox,
  renderLinkField,
  renderIntToDateInputElementField,
  renderBusinessPhoneField,
  renderPhoneNumberField,
  renderPlaceHolderField,
  renderNotesField,
  renderEmptySpace,
} from './textRenderers';
import { renderDateField, renderTimeField } from './dateTimeRenderers';
import {
  renderCheckboxField,
  renderOnOffField,
  renderOnOffCheckBoxField,
  renderNumberWithTwoDecimalsCheckBox,
  renderBoolField,
  renderEmptyYesNoSelect,
  renderSelectFieldCheckBox,
} from './booleanRenderers';
import {
  renderLookupSelect,
  renderLookupSelectForTpClientPaymentMethod,
  renderLookupMultiChoice,
  renderLookupItemMiscMultiChoice,
  renderPredefinedEvaluatorsMultiChoice,
} from './lookupRenderers';
import {
  renderLanguageMultiSelectField,
  renderOfficeMultiSelectField,
  renderOfficeForReportMultiSelectField,
  renderOfficeGroupMultiSelectField,
  renderTpJobThirdPartyStatusMultiSelectField,
  renderTpJobStatusMultiSelectField,
  renderClientMultiChoice,
  renderClientMultiChoiceAll,
  renderThirdPartySourceMultiChoice,
  renderThirdPartySourceEvaluatorMultiChoice,
  renderThirdPartySourceTpJobMultiChoice,
  renderActiveGroupMultiChoice,
  renderCountryMultiChoice,
  renderMultiChoice,
  renderDataFlowMapMultiChoice,
  renderFreelancerDepartmentMultiChoice,
  renderServiceMultiChoice,
  renderContactMultiChoice,
  renderOpportunityMultiChoice,
  renderCommissionUserMultiChoice,
  renderUserMultiSelect,
  renderCaspianRolesMultiCheck,
  renderTimesheetUserListSelect,
} from './multiChoiceRenderers';
import {
  renderSelectAddressWithInformationByEntityType,
  renderCountrySelect,
  renderCurrencySelect,
  renderClientSelect,
  renderSurveySelect,
  renderServiceSelect,
  renderWaveSelect,
  renderOfficeSelect,
  renderBilledOfficeSelect,
  renderOfficeDetailsGroupSelect,
  renderOfficeGroupSelect,
  renderOfficeOrGroupSelect,
  renderPaymentOfficeSelect,
  renderOpportunityStatusChangeSelect,
  renderPricingStatusChangeSelect,
  renderUserSelect,
  renderUserSelectWithGuid,
  renderOutsourcedUserSelect,
  renderLeadSelect,
  renderPrimaryFreelancerUserSelect,
  renderContactSelect,
  renderProjectManagerSelect,
  renderTrueFalseLookupItemSelect,
  renderProjectOpportunitySelect,
  renderProjectServiceSelect,
  renderClientFeeSelect,
  renderProjectSelect,
  renderActiveGroupSelect,
  renderCreditMemoRequestInvoiceSelect,
  renderTpCreditMemoTpClientInvoiceSelect,
  renderCreditMemoRequestStatusChangeSelect,
  renderPaymentRequestStatusChangeSelect,
  renderTpPaymentRequestStatusChangeSelect,
  renderClientForReportSelect,
  renderProjectForReportSelect,
  renderCommissionUserSelect,
  renderExternalSalesPartnerSelect,
  renderThirdPartySourceSelect,
  renderThirdPartySourceTpClientLinkSelect,
  renderFreelancerUserManagerSelect,
  renderSourceSelect,
  renderTpSurveySelect,
  renderTpEvaluatorSelect,
  renderTpSurveyByClientAndInstanceSelect,
  renderTpWaveSelect,
  renderSourceQuestionSelect,
  renderFileUploadField,
} from './selectorRenderers';

/** Fallback used when a type has no dedicated renderer (matches the old `default:` switch arm). */
const renderDefaultTextField = (props) => renderTextField(props, 'text', false);

/**
 * Maps each `InputTypesEnum` value to a renderer `(props) => ReactNode`.
 * This object replaces the former ~300-line `switch` in `renderInputField()`.
 * Several types intentionally share a renderer (the renderer inspects `props.type`
 * to decide its variant — e.g. active vs. all, country code vs. name).
 */
const INPUT_RENDERERS = {
  [InputTypesEnum.Number]: renderNumberStepper,
  [InputTypesEnum.Number2]: renderNumberStepper,
  [InputTypesEnum.NumberWithoutSteppers]: (props) => renderNumberStepper(props, false),
  [InputTypesEnum.TextReadOnly]: (props) => renderTextField(props, 'text', true),
  [InputTypesEnum.Link]: renderLinkField,
  [InputTypesEnum.InputWithCheckBox]: (props) => renderInputWithCheckBox(props),
  [InputTypesEnum.NumberWithCheckBox]: (props) => renderInputWithCheckBox(props, 'number'),
  [InputTypesEnum.Text2]: renderTextField2,
  [InputTypesEnum.TextList]: renderImportListTextField,
  [InputTypesEnum.IntToDateInputElement]: renderIntToDateInputElementField,
  [InputTypesEnum.LanguageMultiSelect]: renderLanguageMultiSelectField,
  [InputTypesEnum.OfficeMultiSelect]: renderOfficeMultiSelectField,
  [InputTypesEnum.OfficeForReportMultiSelect]: renderOfficeForReportMultiSelectField,
  [InputTypesEnum.OfficeGroupMultiSelect]: renderOfficeGroupMultiSelectField,
  [InputTypesEnum.OfficeOrGroupMultiSelect]: renderOfficeOrGroupSelect,
  [InputTypesEnum.OfficeOrGroupForReportMultiSelect]: renderOfficeOrGroupSelect,
  [InputTypesEnum.SelectAddressWithInformationByEntityType]: renderSelectAddressWithInformationByEntityType,
  [InputTypesEnum.BusinessPhone]: renderBusinessPhoneField,
  [InputTypesEnum.PhoneNumber]: renderPhoneNumberField,
  [InputTypesEnum.TextBox]: (props) => renderTextBox(props),
  [InputTypesEnum.TextBox2]: (props) => renderTextBox(props),
  [InputTypesEnum.TextBoxReadOnly]: (props) => renderTextBox(props, true),
  [InputTypesEnum.EmptySpace]: renderEmptySpace,
  [InputTypesEnum.PlaceHolder]: renderPlaceHolderField,
  [InputTypesEnum.NumberWithTwoDecimalPlaces]: renderNumberWithTwoDecimalsCheckBox,
  [InputTypesEnum.NumberWithTwoDecimalPlaces2]: renderNumberWithTwoDecimalsCheckBox,
  [InputTypesEnum.Date]: renderDateField,
  [InputTypesEnum.DateWithIndefinite]: renderDateField,
  [InputTypesEnum.Time]: renderTimeField,
  [InputTypesEnum.Checkbox]: renderCheckboxField,
  [InputTypesEnum.OnOff]: renderOnOffField,
  [InputTypesEnum.ClientMultiChoice]: renderClientMultiChoice,
  [InputTypesEnum.ClientMultiChoiceAll]: renderClientMultiChoiceAll,
  [InputTypesEnum.CountryMultiChoice]: renderCountryMultiChoice,
  [InputTypesEnum.CountryCodeMultiChoice]: renderCountryMultiChoice,
  [InputTypesEnum.CountryNameMultiChoice]: renderCountryMultiChoice,
  [InputTypesEnum.LookupMultiChoice]: renderLookupMultiChoice,
  [InputTypesEnum.LookupTextMultiChoice]: renderLookupMultiChoice,
  [InputTypesEnum.LookupItemMiscMultiChoice]: renderLookupItemMiscMultiChoice,
  [InputTypesEnum.PredefinedTpEvaluatorsMultiChoice]: renderPredefinedEvaluatorsMultiChoice,
  [InputTypesEnum.PredefinedEvaluatorsMultiChoice]: renderPredefinedEvaluatorsMultiChoice,
  [InputTypesEnum.MultiChoice]: renderMultiChoice,
  [InputTypesEnum.Notes]: renderNotesField,
  [InputTypesEnum.SingleCheckBox]: renderOnOffCheckBoxField,
  [InputTypesEnum.Select]: renderSelectFieldCheckBox,
  [InputTypesEnum.Select2]: renderSelectFieldCheckBox,
  [InputTypesEnum.EmptyYesNoSelect]: renderEmptyYesNoSelect,
  [InputTypesEnum.EmptyYesNoSelect2]: renderEmptyYesNoSelect,
  [InputTypesEnum.Bool]: renderBoolField,
  [InputTypesEnum.CountrySelect]: renderCountrySelect,
  [InputTypesEnum.CountryCodeSelect]: renderCountrySelect,
  [InputTypesEnum.CountryNameSelect]: renderCountrySelect,
  [InputTypesEnum.CurrencySelect]: renderCurrencySelect,
  [InputTypesEnum.ActiveCurrencySelect]: renderCurrencySelect,
  [InputTypesEnum.ClientSelect]: renderClientSelect,
  [InputTypesEnum.SurveySelect]: renderSurveySelect,
  [InputTypesEnum.ClientPaymentTypeLookupTextSelectWithoutPrepayment]: renderLookupSelectForTpClientPaymentMethod,
  [InputTypesEnum.ServiceSelect]: renderServiceSelect,
  [InputTypesEnum.ActiveServiceSelect]: renderServiceSelect,
  [InputTypesEnum.WaveSelect]: renderWaveSelect,
  [InputTypesEnum.OfficeSelect]: renderOfficeSelect,
  [InputTypesEnum.BilledOfficeSelect]: renderBilledOfficeSelect,
  [InputTypesEnum.OfficeDetailsGroupSelect]: renderOfficeDetailsGroupSelect,
  [InputTypesEnum.OfficeGroupSelect]: renderOfficeGroupSelect,
  [InputTypesEnum.OfficeOrGroupSelect]: renderOfficeOrGroupSelect,
  [InputTypesEnum.PaymentOfficeSelect]: renderPaymentOfficeSelect,
  [InputTypesEnum.OpportunityStatusChange]: renderOpportunityStatusChangeSelect,
  [InputTypesEnum.PricingStatusChange]: renderPricingStatusChangeSelect,
  [InputTypesEnum.UserSelect]: renderUserSelect,
  [InputTypesEnum.ActiveUserSelect]: renderUserSelect,
  [InputTypesEnum.UserSelectWithGuid]: renderUserSelectWithGuid,
  [InputTypesEnum.ActiveUserSelectWithGuid]: renderUserSelectWithGuid,
  [InputTypesEnum.OutsourcedUserSelect]: renderOutsourcedUserSelect,
  [InputTypesEnum.UserMultiSelect]: renderUserMultiSelect,
  [InputTypesEnum.ActiveUserMultiSelect]: renderUserMultiSelect,
  [InputTypesEnum.LeadSelect]: renderLeadSelect,
  [InputTypesEnum.PrimaryFreelancerUserSelect]: renderPrimaryFreelancerUserSelect,
  [InputTypesEnum.ContactSelect]: renderContactSelect,
  [InputTypesEnum.ActiveContactSelect]: renderContactSelect,
  [InputTypesEnum.ContactSelectForNewInvoice]: renderContactSelect,
  [InputTypesEnum.ProjectManagerSelect]: renderProjectManagerSelect,
  [InputTypesEnum.InvoiceFormTypeLookupItemSelect]: (props) => renderTrueFalseLookupItemSelect(props, 79),
  [InputTypesEnum.ProjectOpportunitySelect]: renderProjectOpportunitySelect,
  [InputTypesEnum.ProjectServiceSelect]: renderProjectServiceSelect,
  [InputTypesEnum.ProjectSelect]: renderClientFeeSelect,
  [InputTypesEnum.ActiveClientFeeSelect]: renderClientFeeSelect,
  [InputTypesEnum.ActiveProjectSelect]: renderProjectSelect,
  [InputTypesEnum.CreditMemoRequestInvoiceSelect]: renderCreditMemoRequestInvoiceSelect,
  [InputTypesEnum.TpCreditMemoTpClientInvoiceSelect]: renderTpCreditMemoTpClientInvoiceSelect,
  [InputTypesEnum.CreditMemoRequestStatusChange]: renderCreditMemoRequestStatusChangeSelect,
  [InputTypesEnum.PaymentRequestStatusChangeSelect]: renderPaymentRequestStatusChangeSelect,
  [InputTypesEnum.TpPaymentRequestStatusChangeSelect]: renderTpPaymentRequestStatusChangeSelect,
  [InputTypesEnum.ClientForReportSelect]: renderClientForReportSelect,
  [InputTypesEnum.ProjectForReportSelect]: renderProjectForReportSelect,
  [InputTypesEnum.FileUploadField]: renderFileUploadField,
  [InputTypesEnum.DataFlowMapMultiChoice]: renderDataFlowMapMultiChoice,
  [InputTypesEnum.FreelancerDepartmentMultiChoice]: renderFreelancerDepartmentMultiChoice,
  [InputTypesEnum.CaspianRolesMultiCheck]: renderCaspianRolesMultiCheck,
  [InputTypesEnum.TimesheetUserListSelect]: renderTimesheetUserListSelect,
  [InputTypesEnum.CommissionUserMultiChoice]: renderCommissionUserMultiChoice,
  [InputTypesEnum.CommissionUserSelect]: renderCommissionUserSelect,
  [InputTypesEnum.OpportunityMultiChoice]: renderOpportunityMultiChoice,
  [InputTypesEnum.ServiceMultiChoice]: renderServiceMultiChoice,
  [InputTypesEnum.ContactMultiChoice]: renderContactMultiChoice,
  [InputTypesEnum.TpJobThirdPartyStatusMultiSelect]: renderTpJobThirdPartyStatusMultiSelectField,
  [InputTypesEnum.TpJobStatusMultiSelect]: renderTpJobStatusMultiSelectField,
  [InputTypesEnum.ActiveExternalSalesPartnerSelect]: renderExternalSalesPartnerSelect,
  [InputTypesEnum.ExternalSalesPartnerSelect]: renderExternalSalesPartnerSelect,
  [InputTypesEnum.ThirdPartySourceSelect]: renderThirdPartySourceSelect,
  [InputTypesEnum.ThirdPartySourceTpClientLinkSelect]: renderThirdPartySourceTpClientLinkSelect,
  [InputTypesEnum.ThirdPartySourceMultiChoice]: renderThirdPartySourceMultiChoice,
  [InputTypesEnum.ThirdPartySourceEvaluatorMultiChoice]: renderThirdPartySourceEvaluatorMultiChoice,
  [InputTypesEnum.ThirdPartySourceTpJobMultiChoice]: renderThirdPartySourceTpJobMultiChoice,
  [InputTypesEnum.FreelancerUserManagerSelect]: renderFreelancerUserManagerSelect,
  [InputTypesEnum.ActiveGroupMultiChoice]: renderActiveGroupMultiChoice,
  [InputTypesEnum.ActiveGroupSelect]: renderActiveGroupSelect,
  [InputTypesEnum.SourceSelect]: renderSourceSelect,
  [InputTypesEnum.TpSurveySelect]: renderTpSurveySelect,
  [InputTypesEnum.TpEvaluatorSelect]: renderTpEvaluatorSelect,
  [InputTypesEnum.TpSurveyByClientAndInstanceSelect]: renderTpSurveyByClientAndInstanceSelect,
  [InputTypesEnum.TpWaveSelect]: renderTpWaveSelect,
  [InputTypesEnum.SourceQuestionSelect]: renderSourceQuestionSelect,
};

// Add every generic lookup-item select declared in the config table.
Object.entries(LOOKUP_SELECT_CONFIG).forEach(([type, config]) => {
  INPUT_RENDERERS[type] = (props) => renderLookupSelect(props, config);
});

/** Returns the renderer for a given input type, falling back to a plain text field. */
export const getInputRenderer = (type) => INPUT_RENDERERS[type] ?? renderDefaultTextField;

export default INPUT_RENDERERS;
